// Analytics layer: Google Tag Manager (GA4) + Microsoft Clarity.
//
// The GTM and Clarity loader snippets are injected into index.html at build
// time by `analyticsHtmlPlugin` in vite.config.ts, gated on the public env
// vars VITE_GTM_ID and VITE_CLARITY_ID. This module is the typed interface for
// pushing conversion events to the dataLayer. When GTM is not configured, the
// events simply queue on `window.dataLayer` and are never consumed — a safe
// no-op.

export type AnalyticsEvent =
  | "apply_form_started"
  | "apply_form_step_completed"
  | "apply_form_submitted"
  | "lead_form_started"
  | "lead_form_submitted"
  | "phone_click"
  | "industry_card_click"
  | "cta_apply_now_click";

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    clarity?: (...args: unknown[]) => void;
  }
}

let initialized = false;

/**
 * Boot analytics. Idempotent and browser-only. Call once on client startup.
 * The third-party tags load from index.html; this just ensures the dataLayer
 * array exists so early `trackEvent` calls are not lost.
 */
export function initAnalytics(): void {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  window.dataLayer = window.dataLayer || [];
}

/**
 * Push a conversion event to the GTM dataLayer. Safe to call even when GTM is
 * not configured — the event is still queued on dataLayer for later flushing.
 */
export function trackEvent(
  event: AnalyticsEvent,
  params: Record<string, unknown> = {},
): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...params });
}
